import { useMemo } from 'react'
import type { ComponentType } from 'react'
import type { FeatureFlagKeyType } from '@scrambl/shared'
import type { NavItem } from '@/config/navigation'
import { useNavigationItems } from './useNavigationItems'
import { useAccountType } from './useAccountType'
import { useFeatureFlags } from './useFeatureFlags'
import { usePermissions } from './usePermissions'
import { useSubscriptionFeatures } from './useSubscriptionFeatures'
import { useOrganizationStore } from '@/store/organization.store'
import {
  ADMIN_SETTINGS_SECTIONS,
  MODULE_SETTINGS_SECTIONS,
  USER_SETTINGS_TABS,
} from '@/config/settings-sections'

export interface OmniNavTarget {
  id: string
  label: string
  /** Org-relative path — pass through getOrgPath() before navigating */
  path: string
  icon: ComponentType<{ className?: string }>
  group: 'module' | 'settings'
  keywords: string[]
}

// Extra search aliases per module href so "vendors" finds Suppliers, etc.
const MODULE_KEYWORDS: Record<string, string[]> = {
  '/today': ['overview', 'home', 'dashboard'],
  '/tasks/mine': ['my work', 'my day', 'tasks'],
  '/notes/mine': ['notes'],
  '/my-time': ['time tracking', 'timer', 'hours'],
  '/calendar': ['schedule', 'planner'],
  '/my-organization': ['home base', 'organization', 'org'],
  '/projects': ['folders'],
  '/tasks': ['todo', 'work'],
  '/tickets': ['helpdesk', 'support'],
  '/knowledge-base': ['kb', 'articles', 'wiki', 'help'],
  '/collections': ['database', 'lists'],
  '/forms': ['surveys'],
  '/threads': ['discussions', 'chat'],
  '/documents': ['files', 'docs'],
  '/events': ['calendar'],
  '/contacts': ['crm', 'people', 'address book', 'leads'],
  '/deals': ['pipeline', 'sales', 'crm'],
  '/marketing': ['campaigns', 'email', 'journeys'],
  '/scheduling': ['booking', 'appointments'],
  '/live-chat': ['chat', 'widget'],
  '/quotes': ['proposals', 'estimates'],
  '/products': ['price book', 'catalog'],
  '/communications': ['calls', 'calling', 'phone', 'sequences'],
  '/entities': ['clients', 'companies', 'accounts'],
  '/suppliers': ['vendors'],
  '/team': ['members', 'staff', 'people'],
  '/timesheets': ['time approval', 'hours'],
  '/workflows': ['automation'],
  '/contracts': ['agreements'],
  '/invoices': ['billing'],
  '/expenses': ['receipts', 'spend'],
}

// Module hrefs that have a dedicated settings page (routes verified in
// organization-routes.tsx). Value is the org-relative settings path.
// Note: /notes/settings is a dead redirect and /tasks/settings just
// redirects to /tasks/mine/settings, so neither is listed.
const MODULE_SETTINGS_PATHS: Record<string, string> = {
  '/tasks/mine': '/tasks/mine/settings',
  '/calendar': '/calendar/settings',
  '/projects': '/projects/settings',
  '/tickets': '/tickets/settings',
  '/events': '/events/settings',
  '/contacts': '/contacts/settings',
  '/deals': '/deals/settings',
  '/entities': '/entities/settings',
  '/suppliers': '/suppliers/settings',
  '/team': '/team/settings',
  '/timesheets': '/timesheets/settings',
  '/forms': '/forms/settings',
  '/contracts': '/contracts/settings',
  '/invoices': '/invoices/settings',
  '/expenses': '/expenses/settings',
}

const ADMIN_LABELS: Record<string, string> = {
  '/admin/members': 'Organization Members',
  '/admin/integrations': 'Integrations',
  '/admin/settings': 'Organization Settings',
  '/admin/subscription': 'Subscription & Billing',
}

const ADMIN_KEYWORDS: Record<string, string[]> = {
  '/admin/members': ['admin', 'users', 'invite', 'roles', 'permissions'],
  '/admin/integrations': ['admin', 'connect', 'slack', 'github', 'quickbooks', 'outlook'],
  '/admin/settings': ['admin', 'general', 'workspace', 'organization', 'finance'],
  '/admin/subscription': ['admin', 'billing', 'plan', 'payment', 'upgrade', 'seats'],
}

/**
 * Navigation destinations (modules + settings pages) searchable from
 * OmniSearch. Applies the same visibility gating as AppSidebar
 * (accountTypes → featureFlag, else subscriptionFeature → permission), but
 * includes `hidden` nav items — search is exactly where non-sidebar
 * destinations should stay discoverable.
 */
export function useOmniNavTargets(): OmniNavTarget[] {
  const { mainNavItems, adminNavItems, userMenuItems } = useNavigationItems()
  const { accountType, isPersonal } = useAccountType()
  const { checkPermission } = usePermissions()
  const { checkFeature } = useSubscriptionFeatures()
  const { currentOrganization } = useOrganizationStore()

  const featureKeys = useMemo(
    () =>
      mainNavItems
        .map(item => item.featureFlag)
        .filter(Boolean) as FeatureFlagKeyType[],
    [mainNavItems],
  )
  const { checkFeatureFlag } = useFeatureFlags(featureKeys)

  const membershipRole = currentOrganization?.userMembership?.role
  const isAdminOrOwner = membershipRole === 'OWNER' || membershipRole === 'ADMIN'

  return useMemo(() => {
    // Mirrors AppSidebar's filteredNavItems: an explicitly enabled feature
    // flag wins over the subscription gate.
    const isItemVisible = (item: NavItem) => {
      if (item.accountTypes && !item.accountTypes.includes(accountType)) {
        return false
      }
      if (item.featureFlag) {
        if (!checkFeatureFlag(item.featureFlag)) return false
      } else if (
        item.subscriptionFeature &&
        !checkFeature(item.subscriptionFeature)
      ) {
        return false
      }
      if (
        item.permission &&
        !checkPermission(item.permission.module, item.permission.action)
      ) {
        return false
      }
      return true
    }

    const visibleModules = mainNavItems.filter(isItemVisible)

    const moduleTargets: OmniNavTarget[] = visibleModules.map(item => ({
      id: `module:${item.href}`,
      label: item.label,
      path: item.href,
      icon: item.icon,
      group: 'module',
      keywords: MODULE_KEYWORDS[item.href] ?? [],
    }))

    const settingsTargets: OmniNavTarget[] = []

    // Per-module settings pages, gated by the module's own visibility.
    // Section entries deep-link via ?section= (pages resolve it through
    // useSettingsSection or their own param handling).
    for (const item of visibleModules) {
      const settingsPath = MODULE_SETTINGS_PATHS[item.href]
      if (!settingsPath) continue
      settingsTargets.push({
        id: `settings:${settingsPath}`,
        label: `${item.label} Settings`,
        path: settingsPath,
        icon: item.icon,
        group: 'settings',
        keywords: [
          'settings',
          'preferences',
          'configure',
          ...(MODULE_KEYWORDS[item.href] ?? []),
        ],
      })

      const sectionConfig = MODULE_SETTINGS_SECTIONS[item.href]
      if (!sectionConfig) continue
      const param = sectionConfig.param ?? 'section'
      for (const section of sectionConfig.sections) {
        settingsTargets.push({
          id: `settings:${settingsPath}:${section.id}`,
          label: `${item.label} · ${section.label}`,
          path: `${settingsPath}?${param}=${section.id}`,
          icon: item.icon,
          group: 'settings',
          // No generic 'settings' keyword here — a bare "settings" query
          // should surface pages, not flood with every section.
          keywords: [
            ...(section.keywords ?? []),
            ...(MODULE_KEYWORDS[item.href] ?? []),
          ],
        })
      }
    }

    for (const item of userMenuItems) {
      settingsTargets.push({
        id: `settings:${item.href}`,
        label: item.label,
        path: item.href,
        icon: item.icon,
        group: 'settings',
        keywords:
          item.href === '/user-settings'
            ? ['settings', 'profile', 'preferences', 'integrations', 'skills']
            : ['settings', 'account', 'password', 'email'],
      })
      if (item.href === '/user-settings') {
        for (const tab of USER_SETTINGS_TABS) {
          settingsTargets.push({
            id: `settings:/user-settings:${tab.id}`,
            label: `My Profile · ${tab.label}`,
            path: `/user-settings#${tab.id}`,
            icon: item.icon,
            group: 'settings',
            keywords: tab.keywords ?? [],
          })
        }
      }
    }

    if (isAdminOrOwner) {
      for (const item of adminNavItems) {
        if (item.accountTypes && !item.accountTypes.includes(accountType)) {
          continue
        }
        settingsTargets.push({
          id: `settings:${item.href}`,
          label: ADMIN_LABELS[item.href] ?? item.label,
          path: item.href,
          icon: item.icon,
          group: 'settings',
          keywords: ['settings', ...(ADMIN_KEYWORDS[item.href] ?? [])],
        })
        if (item.href === '/admin/settings') {
          for (const section of ADMIN_SETTINGS_SECTIONS) {
            if (section.businessOnly && isPersonal) continue
            if (section.personalOnly && !isPersonal) continue
            settingsTargets.push({
              id: `settings:/admin/settings:${section.id}`,
              label: `Organization Settings · ${section.label}`,
              path: `/admin/settings?section=${section.id}`,
              icon: item.icon,
              group: 'settings',
              keywords: section.keywords ?? [],
            })
          }
        }
      }
    }

    return [...moduleTargets, ...settingsTargets]
  }, [
    accountType,
    adminNavItems,
    checkFeature,
    checkFeatureFlag,
    checkPermission,
    isAdminOrOwner,
    isPersonal,
    mainNavItems,
    userMenuItems,
  ])
}

/**
 * Match nav targets against a search query. Ranked: label prefix, word
 * prefix, label substring, keyword prefix, keyword substring. Single-char
 * queries only match label prefixes to keep noise down.
 */
export function matchNavTargets(
  targets: OmniNavTarget[],
  query: string,
): OmniNavTarget[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const scored: { target: OmniNavTarget; score: number }[] = []

  for (const target of targets) {
    const label = target.label.toLowerCase()
    let score: number | null = null

    if (label.startsWith(q)) {
      score = 0
    } else if (q.length >= 2) {
      if (label.split(/\s+/).some(word => word.startsWith(q))) {
        score = 1
      } else if (label.includes(q)) {
        score = 2
      } else if (target.keywords.some(k => k.startsWith(q))) {
        score = 3
      } else if (target.keywords.some(k => k.includes(q))) {
        score = 4
      } else {
        // Multi-word queries: every token must appear somewhere.
        const tokens = q.split(/\s+/)
        if (tokens.length > 1) {
          const haystack = `${label} ${target.keywords.join(' ')}`
          if (tokens.every(token => haystack.includes(token))) {
            score = 5
          }
        }
      }
    }

    if (score !== null) {
      scored.push({ target, score })
    }
  }

  return scored
    .sort(
      (a, b) =>
        a.score - b.score || a.target.label.length - b.target.label.length,
    )
    .map(entry => entry.target)
}
